import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Timer, X, SkipForward, Play } from 'lucide-react';
import { Button } from './ui/button';
import api from '../utils/api';

export const SESSION_KEY = 'iling_study_session';

export function readSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY)); } catch { return null; }
}
export function writeSession(s) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  window.dispatchEvent(new CustomEvent('study-session-updated'));
}
export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
  window.dispatchEvent(new CustomEvent('study-session-updated'));
}

// ── Canvas confetti ─────────────────────────────────────────────────────────
function Confetti({ active }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const COLORS = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#F97316'];
    const particles = Array.from({ length: 180 }, () => ({
      x: Math.random() * canvas.width,
      y: -20 - Math.random() * 100,
      vx: (Math.random() - 0.5) * 5,
      vy: Math.random() * 3 + 2,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      w: Math.random() * 10 + 5,
      h: Math.random() * 6 + 3,
      angle: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.2,
    }));

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy; p.vy += 0.06; p.angle += p.spin;
        if (p.y < canvas.height + 30) alive = true;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (alive) animRef.current = requestAnimationFrame(draw);
    }
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [active]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 9998, width: '100vw', height: '100vh' }}
    />
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export function FloatingStudyTimer() {
  const navigate = useNavigate();
  const [session, setSession] = useState(readSession);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const intervalRef = useRef(null);

  const current = session?.languages?.[session?.current_index];

  // Sync session when another component fires the custom event
  useEffect(() => {
    const handler = () => {
      const s = readSession();
      setSession(s);
      if (!s) { setSecondsLeft(null); setShowModal(false); clearInterval(intervalRef.current); }
    };
    window.addEventListener('study-session-updated', handler);
    return () => window.removeEventListener('study-session-updated', handler);
  }, []);

  // Start countdown whenever the current language changes
  const startCountdown = useCallback((seconds) => {
    clearInterval(intervalRef.current);
    setSecondsLeft(seconds);
    intervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) { clearInterval(intervalRef.current); setShowModal(true); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    clearInterval(intervalRef.current);
    setShowModal(false);
    if (!current || current.visited) { setSecondsLeft(null); return; }
    startCountdown((current.minutes || 5) * 60);
    return () => clearInterval(intervalRef.current);
  }, [current?.language_id, startCountdown]); // eslint-disable-line

  const handleContinue = () => {
    setShowModal(false);
    startCountdown((current?.minutes || 5) * 60);
  };

  const goToLanguage = useCallback(async (langId) => {
    try {
      const res = await api.get('/texts', { params: { language_id: langId, limit: 1 } });
      const texts = res.data || [];
      if (texts.length > 0) {
        navigate(`/reader/${texts[0].id || texts[0].text_id}`);
      } else {
        navigate('/texts');
      }
    } catch {
      navigate('/texts');
    }
  }, [navigate]);

  const handleNext = async () => {
    setShowModal(false);
    if (!session) return;

    const langs = session.languages.map((l, i) =>
      i === session.current_index ? { ...l, visited: true } : l
    );
    const nextIdx = langs.findIndex((l, i) => i > session.current_index && !l.visited);

    if (nextIdx === -1) {
      writeSession({ ...session, languages: langs });
      setSession(null);
      setSecondsLeft(null);
      setShowCongrats(true);
      return;
    }

    const newSession = { ...session, languages: langs, current_index: nextIdx };
    writeSession(newSession);
    setSession(newSession);
    await goToLanguage(langs[nextIdx].language_id);
  };

  const handleDismiss = () => {
    clearInterval(intervalRef.current);
    clearSession();
    setSession(null);
    setSecondsLeft(null);
    setShowModal(false);
  };

  const handleCongratsClose = () => {
    clearSession();
    setShowCongrats(false);
    navigate('/dashboard');
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const nextLang = session?.languages?.find((l, i) => i > (session?.current_index ?? -1) && !l.visited);
  const doneCount = (session?.languages || []).filter(l => l.visited).length;
  const totalCount = session?.languages?.length || 0;

  if (!showCongrats && (!session || !current || current.visited)) return null;

  return (
    <>
      <Confetti active={showCongrats} />

      {/* Floating pill — always visible while session is active */}
      {!showModal && !showCongrats && secondsLeft !== null && (
        <div
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-background border border-border shadow-xl"
          data-testid="study-timer-pill"
        >
          <Timer className="h-4 w-4 text-primary shrink-0" />
          <span className="text-sm font-mono font-bold tabular-nums">{fmt(secondsLeft)}</span>
          <span className="text-xs text-muted-foreground max-w-[120px] truncate">{current.name}</span>
          {totalCount > 1 && (
            <span className="text-[10px] text-muted-foreground/60">{doneCount + 1}/{totalCount}</span>
          )}
          <button
            onClick={handleDismiss}
            className="ml-1 text-muted-foreground hover:text-foreground transition-colors"
            title="End session"
            data-testid="study-timer-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Time's-up modal */}
      {showModal && !showCongrats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="bg-background border border-border rounded-2xl shadow-2xl p-6 w-80 space-y-4"
            data-testid="study-timer-modal"
          >
            <div className="text-center space-y-1">
              <div className="text-3xl mb-2">⏱</div>
              <h3 className="font-semibold text-lg">Time's up for {current.name}!</h3>
              <p className="text-sm text-muted-foreground">
                Your {current.minutes}-minute session is done. What would you like to do?
              </p>
            </div>
            <div className="space-y-2">
              <Button className="w-full gap-2" onClick={handleContinue} data-testid="study-timer-continue">
                <Play className="h-4 w-4" />
                Continue this session
              </Button>
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={handleNext}
                data-testid="study-timer-next"
              >
                <SkipForward className="h-4 w-4" />
                {nextLang ? `Next: ${nextLang.name}` : 'Finish study plan'}
              </Button>
            </div>
            <button
              onClick={handleDismiss}
              className="w-full text-xs text-muted-foreground hover:text-foreground text-center py-1 transition-colors"
              data-testid="study-timer-end"
            >
              End session
            </button>
          </div>
        </div>
      )}

      {/* Congratulations overlay */}
      {showCongrats && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/60"
          style={{ zIndex: 9999 }}
          data-testid="study-congrats-overlay"
        >
          <div className="bg-background border border-border rounded-2xl shadow-2xl p-8 w-96 text-center space-y-4">
            <div className="text-5xl">🎉</div>
            <h2 className="text-2xl font-bold">Study plan complete!</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              You've worked through all {totalCount > 0 ? totalCount : 'your'} languages in today's study plan. Great work — keep it up!
            </p>
            <Button className="w-full" onClick={handleCongratsClose} data-testid="study-congrats-close">
              Back to Dashboard
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
